import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { SectionBoundary } from "@/components/SectionBoundary";
import {
  ShieldCheck, BadgeCheck, SlidersHorizontal, Lock, ScanSearch, Truck,
  ArrowRight, Crosshair, Target, ZoomIn, Moon, Ruler, Telescope, Wrench,
  Package, Settings, Layers, Plus, Minus, Sword, Grip, Plane,
} from "lucide-react";

/* ---- exact landing tokens (match site + /join brand) ---------------------- */
const C = {
  page: "#050506", panel: "#111213", inset: "#0D0E0F", border: "#2E2F31", field: "#3E3F42",
  text: "#E8E8EA", muted: "#8B8D90", dim: "#6B6D70",
  olive: "#57682A", oliveLight: "#748A3D", lime: "#cfe86b", gold: "#E8C46A",
};
import { LOGO, HERO } from "@/assets";
import { usePageContent, AccentText } from "@/lib/pageContent";

// Topo texture now comes from ONE global backdrop (.os-page in App.js) that rides
// under every section. Sections are transparent so that single contour carries
// through the whole page — EXCEPT the Founding band, which is a deliberate solid
// olive accent (#12180A) interrupting the topo. One accent band is rhythm; seven
// alternating shades with the texture stopping and starting between them was noise.
const PHOTOS = ["hunter-bear", "coastline", "rifles-flag", "helicopter"];

/* ---- small building blocks ------------------------------------------------ */
const SectionLabel = ({ children }) => (
  <p className="font-mono text-[11px] tracking-[0.14em] uppercase">
    <span style={{ color: C.field }}>// </span>
    <span style={{ color: C.oliveLight }}>{children}</span>
  </p>
);

const OliveBtn = ({ to, children, className = "", ...rest }) => (
  <Link to={to} {...rest}
    className={`mkt-olive inline-flex items-center justify-center gap-2 font-mono uppercase text-[13px] tracking-[0.14em] px-6 py-3 transition-colors ${className}`}
    style={{ backgroundColor: C.olive, color: C.text }}>
    {children}
  </Link>
);

const OutlineBtn = ({ to, href, children, className = "", ...rest }) => {
  const cls = `mkt-outline inline-flex items-center justify-center gap-2 font-mono uppercase text-[13px] tracking-[0.14em] px-6 py-3 border transition-colors ${className}`;
  const style = { borderColor: C.field, color: C.text, backgroundColor: "transparent" };
  return href
    ? <a href={href} {...rest} className={cls} style={style}>{children}</a>
    : <Link to={to} {...rest} className={cls} style={style}>{children}</Link>;
};

const PhotoTile = ({ base, override = "", className = "" }) => (
  <div className={`relative overflow-hidden ${className}`} style={{ backgroundColor: C.inset }}>
    {override ? (
      <img src={override} alt="" className="mkt-photo w-full h-full object-cover" loading="eager" decoding="async" />
    ) : (
      <img
        src={HERO[base].lg}
        srcSet={`${HERO[base].sm} 400w, ${HERO[base].lg} 800w`}
        sizes="(min-width: 1000px) 300px, 24vw"
        alt=""
        className="mkt-photo w-full h-full object-cover"
        loading="eager"
        decoding="async"
      />
    )}
    <div className="absolute inset-x-0 bottom-0 h-1/3 pointer-events-none"
      style={{ background: "linear-gradient(to top, rgba(5,5,6,.75), transparent)" }} />
  </div>
);

const Reticle = ({ className = "", style = {} }) => (
  <svg viewBox="0 0 200 200" className={className} style={style} aria-hidden="true">
    <g fill="none" stroke={C.text} strokeWidth="1.4">
      <circle cx="100" cy="100" r="92" />
      <circle cx="100" cy="100" r="60" />
      <circle cx="100" cy="100" r="6" />
      <line x1="100" y1="0" x2="100" y2="72" />
      <line x1="100" y1="128" x2="100" y2="200" />
      <line x1="0" y1="100" x2="72" y2="100" />
      <line x1="128" y1="100" x2="200" y2="100" />
      {[40, 55, 70, 130, 145, 160].map((p) => <line key={`v${p}`} x1={p} y1="96" x2={p} y2="104" />)}
      {[40, 55, 70, 130, 145, 160].map((p) => <line key={`h${p}`} x1="96" y1={p} x2="104" y2={p} />)}
    </g>
  </svg>
);

const CAT_ICONS = {
  "Rifle Scopes": Crosshair,
  "Red Dots & Reflex Sights": Target,
  "Prism Scopes & Magnifiers": ZoomIn,
  "Night Vision & Thermal Optics": Moon,
  "Rangefinders": Ruler,
  "Binoculars & Spotting Scopes": Telescope,
  "Mounts, Rings & Bases": Wrench,
  "Parts & Accessories": Package,
  "Knives & Blades": Sword,
  "Services": Settings,
  "Barrels": Ruler,
  "Chassis": Layers,
  "Uppers": Wrench,
  "Sights": Target,
  "Triggers": SlidersHorizontal,
  "Handguards": Grip,
  "Drones": Plane,
};

const PART_PARENT_TABS = { Barrels: "barrels", Chassis: "chassis", Uppers: "uppers", Sights: "sights", Triggers: "triggers", Handguards: "handguards", Drones: "drones" };

// Map a marketplace category to its /feed destination (tab + narrowing category).
// NV&Thermal + Services live under the "All" tab with a category filter; every other
// category routes to its dedicated tab, narrowed to that exact category.
const OPTICS_CATS = ["Rifle Scopes", "Red Dots & Reflex Sights", "Prism Scopes & Magnifiers", "Rangefinders", "Binoculars & Spotting Scopes"];
function tileTarget(cat) {
  const p = new URLSearchParams();
  // A part parent routes to its own dedicated feed tab (leaf-expanded server-side).
  if (PART_PARENT_TABS[cat]) { p.set("tab", PART_PARENT_TABS[cat]); return `/feed?${p.toString()}`; }
  let tab = "all";
  if (OPTICS_CATS.includes(cat)) tab = "optics";
  else if (cat === "Mounts, Rings & Bases") tab = "mounts";
  else if (cat === "Parts & Accessories") tab = "accessories";
  else if (cat === "Knives & Blades") tab = "knives";
  p.set("tab", tab);
  p.set("category", cat);
  return `/feed?${p.toString()}`;
}

const CategoryTile = ({ cat }) => {
  const [hover, setHover] = useState(false);
  const Icon = CAT_ICONS[cat] || Layers;
  return (
    <Link
      to={tileTarget(cat)}
      data-testid={`category-tile-${cat}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="flex items-center gap-3 p-4 border transition-colors cursor-pointer"
      style={{ backgroundColor: C.panel, borderColor: hover ? C.oliveLight : C.border }}
    >
      <Icon className="w-5 h-5 shrink-0" strokeWidth={1.5} style={{ color: C.oliveLight }} />
      <span className="font-mono uppercase text-[11px] tracking-[0.08em] leading-tight" style={{ color: C.text }}>{cat}</span>
    </Link>
  );
};

/* ---- HOW IT WORKS (chain + money row) ------------------------------------- */
const BARLOW = "'Barlow Condensed', sans-serif";
const JBMONO = "'JetBrains Mono', monospace";

// Steps 2 & 3 are LIT (#cfe86b) — that is exactly the span where OpticSwap holds the
// money. Steps 1 & 4 stay neutral (#E8E8EA word / #8B8D90 caption).
const HOW_STEPS = [
  { word: "LIST", Icon: Package, caption: "Post your gear with real specs and photos.", lit: false },
  { word: "PAY & HOLD", Icon: Lock, caption: "The buyer pays in full. We hold every dollar.", lit: true },
  { word: "SHIP", Icon: Truck, caption: "Tracked. The carrier confirms delivery.", lit: true },
  { word: "RELEASE", Icon: BadgeCheck, caption: "Confirmed as described. Seller gets paid.", lit: false },
];
// Arrow AFTER step 1 stays neutral; arrows AFTER steps 2 and 3 are lit.
const HOW_ARROWS = ["#3E3F42", "#cfe86b", "#cfe86b"];
const HOW_MONEY = [
  { glyph: "#33353a", text: "Still the buyer's", label: "#4a4c50" },
  { glyph: "#cfe86b", text: "Held by OpticSwap", label: "#8fa254" },
  { glyph: "#cfe86b", text: "Still held in full", label: "#8fa254" },
  { glyph: "#cfe86b", text: "Released to the seller", label: "#8fa254" },
];

function HowStep({ step, testid }) {
  const color = step.lit ? "#cfe86b" : "#E8E8EA";
  const Icon = step.Icon;
  return (
    <div className="min-w-0" data-testid={testid}>
      <div className="flex items-center gap-3" style={{ height: 44 }}>
        <Icon size={26} strokeWidth={1.8} color={color} className="shrink-0" />
        <span className="uppercase whitespace-nowrap leading-[0.95] text-[34px] min-[1120px]:text-[42px]"
          style={{ fontFamily: BARLOW, fontWeight: 700, color }}>
          {step.word}
        </span>
      </div>
      <p className="mt-3 text-[13px] max-w-[215px]" style={{ color: "#8B8D90" }}>{step.caption}</p>
    </div>
  );
}

// 2px line + triangular head, vertically centred on the icon/word row (matched heights).
function ChainArrow({ color }) {
  return (
    <div className="flex items-center" style={{ height: 44 }} aria-hidden="true">
      <div className="flex-1" style={{ height: 2, backgroundColor: color }} />
      <div style={{ width: 0, height: 0, borderTop: "4px solid transparent", borderBottom: "4px solid transparent", borderLeft: `6px solid ${color}` }} />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
export default function Landing() {
  const { user } = useAuth();
  const { get, img: pageImg } = usePageContent("home");
  const [categories, setCategories] = useState([]);
  const [openFaq, setOpenFaq] = useState(0);

  // The landing renders at / for EVERYONE — signed in or out. No redirect to /feed:
  // the logo must always reach the marketing page. Members get to the marketplace via
  // the "Browse Marketplace" header button (and the nav Marketplace link on other pages).

  // Live data only — NO listing/feed/count queries ever run on this page.
  useEffect(() => {
    api.get("/config/categories").then(({ data }) => setCategories(data.categories || [])).catch(() => {});
  }, []);

  const FAQS = [
    { q: "Do you sell firearms?", a: "Not for individual sellers. OpticSwap is optics, optics accessories, and shooting gear for individual listings — firearms and NFA items (suppressors, SBRs, etc.) are prohibited and listings that include them are removed. Firearm sales through licensed FFL/dealer accounts are planned — coming soon." },
    { q: "What if it isn't as described?", a: "Your payment is held until delivery is confirmed. When your item arrives you inspect it before funds are released to the seller — if it's wrong, you open a dispute and our team steps in with the transaction record on hand." },
    { q: "Why do I have to verify my identity?", a: "Every seller is ID-verified before they can take your money. Verification is what lets buyers trust a stranger with a high-value piece of gear, and it keeps repeat bad actors off the platform." },
    // TODO: revisit when metered guest browsing ships — guests will be able to preview a few listings before signing up.
    { q: "Can I browse before joining?", a: "Not during the private beta — you'll need an account to get in. Signing up takes a minute and is what keeps OpticSwap a verified-only marketplace. (We're adding a guest preview soon so you can see a handful of listings before you create an account.)" },
    { q: "What does membership cost?", a: "Pricing is live. A one-time $5 Buyer Pass gets you 7 days of browsing, or join as a member: Standard is $10/mo ($100/yr) with a 6.5% platform fee, and Pro is $25/mo ($250/yr) with a lower 3.5% fee. Every plan can list, buy and sell — see the Pricing page for the full breakdown." },
    { q: "Where does my membership fee go?", a: "Running the platform costs money whether anyone's actively buying or selling — ID verification, secure payment processing, keeping funds held safely until delivery confirms, hosting, all of it. The membership fee covers that baseline so your account stays verified and protected even in a slow month. Buy or sell a couple times and it pays for itself many times over in what you're actually protected against." },
  ];

  return (
    <div className="min-h-screen font-body" style={{ color: C.text }} data-testid="marketing-landing">
      {/* 1 — HEADER (shared global header, identical on every page) */}
      <Navbar />

      <main id="top">
        {/* 2 — HERO */}
        <SectionBoundary>
        <section className="relative overflow-hidden" data-testid="hero-section">
          <Reticle className="hidden min-[1000px]:block absolute -left-40 top-1/2 -translate-y-1/2 w-[720px] h-[720px] pointer-events-none"
            style={{ opacity: 0.025 }} />
          <div className="relative max-w-[1240px] mx-auto px-5 py-16 min-[1000px]:py-24 grid min-[1000px]:grid-cols-2 gap-12 items-center">
            {/* left */}
            <div>
              <div className="mt-6"><SectionLabel>{get("hero_eyebrow", "Verified buyers · verified sellers · protected payments")}</SectionLabel></div>
              <h1 className="mt-3 font-heading font-bold uppercase tracking-tight leading-[1.08] text-4xl sm:text-5xl min-[1000px]:text-6xl" style={{ color: C.text }}>
                <AccentText text={get("hero_heading", "The *money* doesn't move until the gear *arrives.*")} accentStyle={{ color: C.lime }} />
              </h1>
              <p className="mt-5 text-base max-w-[58ch]" style={{ color: C.muted }}>
                {get("hero_subhead", "The marketplace for the shooting, hunting, and tactical communities. Optics, thermals, night vision, and everything that runs them — bought, sold, and traded between ID-verified members, with funds held until delivery confirms.")}
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                {user
                  ? <OliveBtn to="/feed" data-testid="hero-marketplace">{get("hero_cta_member", "Browse Marketplace")} <ArrowRight className="w-4 h-4" /></OliveBtn>
                  : <OliveBtn to="/register" data-testid="hero-create">{get("hero_cta_guest", "Create your account")} <ArrowRight className="w-4 h-4" /></OliveBtn>}
                <OutlineBtn href="#how" data-testid="hero-how">{get("hero_cta_secondary", "See how it works")}</OutlineBtn>
              </div>

              <div className="mt-9 pt-7 border-t grid sm:grid-cols-3 gap-5" style={{ borderColor: C.border }}>
                {[
                  { icon: ShieldCheck, t: "Funds held until delivery" },
                  { icon: BadgeCheck, t: "ID-verified sellers" },
                  { icon: SlidersHorizontal, t: "Purpose-built search filters" },
                ].map((x, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <x.icon className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={1.5} style={{ color: C.olive }} />
                    <span className="font-mono uppercase text-[12px] tracking-[0.1em] leading-snug" style={{ color: C.text }}>{x.t}</span>
                  </div>
                ))}
              </div>

            </div>

            {/* photo grid — ONE render. 4-across strip <1000px, 2×2 grid ≥1000px.
                grid-rows-2 + a definite h-[520px] give the tiles real height so the
                images actually have a box to load into (the old h-full-in-auto-rows
                collapsed to 0×0, which is why lazy images never entered the viewport). */}
            <div className="grid grid-cols-4 gap-2 mt-9 min-[1000px]:grid-cols-2 min-[1000px]:grid-rows-2 min-[1000px]:gap-3 min-[1000px]:mt-0 min-[1000px]:h-[520px]" data-testid="hero-photo-grid">
              {PHOTOS.map((base, i) => (
                <PhotoTile key={base} base={base} override={pageImg(`hero_photo_${i + 1}`, "")} className="aspect-square min-[1000px]:aspect-auto min-[1000px]:h-full" />
              ))}
            </div>
          </div>
        </section>
        </SectionBoundary>

        {/* 3 — WHY */}
        <SectionBoundary>
        <section className="os-hairline scroll-mt-20" data-testid="why-section">
          <div className="max-w-[1240px] mx-auto px-5 py-20">
          <SectionLabel>{get("why_eyebrow", "Why OpticSwap")}</SectionLabel>
          <h2 className="mt-2 font-heading font-bold uppercase tracking-tight leading-[1.08] text-2xl min-[1000px]:text-3xl" style={{ color: C.text }}>
            <AccentText text={get("why_heading", "You work hard for your money.\n*Protect your transactions.*")} accentStyle={{ color: C.lime }} />
          </h2>
          <div className="mt-9 grid min-[1000px]:grid-cols-3 gap-4">
            {[
              { icon: Lock, t: "Payment Protection, not the honor system", d: "The buyer's money is captured up front and held by OpticSwap. The seller only gets paid after the buyer confirms the gear arrived as described.", note: "No wires to strangers" },
              { icon: BadgeCheck, t: "Everybody is verified", d: "Sellers pass identity verification before they can accept a payment. Real names, real accountability — the thing generic gear boards can't give you.", note: "ID-checked sellers" },
              { icon: ScanSearch, t: "Search that knows the gear", d: "Filter by reticle, focal plane, tube size, MOA/MRAD, magnification and mount spec — not a free-text guess. Find the exact configuration you're after.", note: "Spec-aware filters" },
            ].map((x, i) => (
              <div key={i} className="flex flex-col p-6 border" style={{ backgroundColor: C.panel, borderColor: C.border }} data-testid={`why-card-${i}`}>
                <div className="w-11 h-11 border flex items-center justify-center" style={{ borderColor: C.field }}>
                  <x.icon className="w-5 h-5" strokeWidth={1.5} style={{ color: C.oliveLight }} />
                </div>
                <h3 className="mt-4 font-heading font-bold uppercase tracking-tight text-xl" style={{ color: C.text }}>{x.t}</h3>
                <p className="mt-2 text-sm flex-1" style={{ color: C.muted }}>{x.d}</p>
                <p className="mt-5 pt-3 border-t font-mono uppercase text-[11px] tracking-[0.14em]" style={{ borderColor: C.border, color: C.oliveLight }}>{x.note}</p>
              </div>
            ))}
          </div>
          </div>
        </section>
        </SectionBoundary>

        {/* 4 — HOW IT WORKS */}
        <SectionBoundary>
        <section id="how" className="os-hairline scroll-mt-20" data-testid="how-it-works">
          <div className="max-w-[1240px] mx-auto px-5 py-20">
          <SectionLabel>How it works</SectionLabel>
          <h2 className="mt-2 font-heading font-bold uppercase tracking-tight text-3xl min-[1000px]:text-4xl" style={{ color: C.text }}>List. Hold. Ship. Release.</h2>

          {/* ROWS 1+2 — ONE shared 4-column grid (repeat(4,1fr)) used by BOTH rows so
              each $$$ block sits dead-center under its step title. Full-width divider
              spans 1/-1; arrows overlay the column gaps, centered on the title row. */}
          <div className="hidden min-[820px]:block relative mt-10" data-testid="how-grid">
            <div className="grid" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
              {/* row 1 — the chain */}
              {HOW_STEPS.map((s, i) => {
                const color = s.lit ? "#cfe86b" : "#E8E8EA";
                const Icon = s.Icon;
                return (
                  <div key={s.word} className="flex flex-col items-center text-center px-2" data-testid={`how-step-${i}`}>
                    <div className="flex items-center justify-center" style={{ height: 44 }}>
                      <Icon size={30} strokeWidth={1.8} color={color} />
                    </div>
                    <div className="flex items-center justify-center" style={{ height: 44 }}>
                      <span className="uppercase whitespace-nowrap leading-[0.95] text-[34px] min-[1120px]:text-[42px]"
                        style={{ fontFamily: BARLOW, fontWeight: 700, letterSpacing: "0.02em", color }}>{s.word}</span>
                    </div>
                    <p className="mt-3 text-[13px] max-w-[215px]" style={{ color: "#8B8D90" }}>{s.caption}</p>
                  </div>
                );
              })}
              {/* full-width divider spanning all four columns */}
              <div className="col-span-full my-[22px]" style={{ borderTop: "1px solid #2E2F31" }} data-testid="how-divider" />
              {/* row 2 — the money row, each block centered under its step */}
              {HOW_MONEY.map((m, i) => (
                <div key={i} className="flex flex-col items-center text-center px-2" data-testid={`how-money-${i}`}>
                  <span className="leading-none" style={{ fontFamily: BARLOW, fontWeight: 700, fontSize: 46, color: m.glyph }}>$$$</span>
                  <span className="mt-2 uppercase" style={{ fontFamily: JBMONO, fontSize: "9.5px", letterSpacing: "0.14em", color: m.label }}>{m.text}</span>
                </div>
              ))}
            </div>
            {/* arrows at the 3 column gaps (25/50/75%), vertically centered on the title row */}
            <div aria-hidden="true" className="absolute left-0 right-0 pointer-events-none" style={{ top: 44, height: 44 }} data-testid="how-arrows">
              {[25, 50, 75].map((pct, i) => (
                <div key={i} className="absolute flex items-center" style={{ left: `${pct}%`, top: "50%", width: 48, transform: "translate(-50%, -50%)" }}>
                  <div className="flex-1" style={{ height: 2, backgroundColor: HOW_ARROWS[i] }} />
                  <div style={{ width: 0, height: 0, borderTop: "4px solid transparent", borderBottom: "4px solid transparent", borderLeft: `6px solid ${HOW_ARROWS[i]}` }} />
                </div>
              ))}
            </div>
          </div>

          {/* Stacked steps (<820px): steps only — no arrows, no money row */}
          <div className="min-[820px]:hidden mt-9 flex flex-col gap-9">
            {HOW_STEPS.map((s, i) => (
              <HowStep key={s.word} step={s} testid={`how-step-m-${i}`} />
            ))}
          </div>
          </div>
        </section>
        </SectionBoundary>

        {/* 5 — FEES: intentionally hidden. The seller commission schedule + per-sale
            minimum are ADMIN-ONLY (never exposed publicly by /api/config/fees), so there
            is no LIVE source for an all-in "$600 sale" comparison. Per spec, when the
            config values are absent we hide the section entirely rather than hardcode. */}

        {/* 7 — CATEGORIES */}
        {categories.length > 0 && (
          <SectionBoundary>
          <section className="os-hairline" data-testid="categories-section">
            <div className="max-w-[1240px] mx-auto px-5 py-14">
            <SectionLabel>What trades here</SectionLabel>
            <h2 className="mt-2 font-heading font-bold uppercase tracking-tight text-3xl min-[1000px]:text-4xl" style={{ color: C.text }}>Categories</h2>
            <div className="mt-9 grid grid-cols-2 sm:grid-cols-3 min-[1000px]:grid-cols-5 gap-3" data-testid="category-tiles">
              {categories.map((cat) => <CategoryTile key={cat} cat={cat} />)}
            </div>
            </div>
          </section>
          </SectionBoundary>
        )}

        {/* 8 — FAQ */}
        <SectionBoundary>
        <section id="faq" className="os-hairline scroll-mt-20" data-testid="faq-section">
          <div className="max-w-[860px] mx-auto px-5 py-20">
          <SectionLabel>Questions</SectionLabel>
          <h2 className="mt-2 font-heading font-bold uppercase tracking-tight text-3xl min-[1000px]:text-4xl" style={{ color: C.text }}>Before you join</h2>
          <div className="mt-9 border divide-y" style={{ backgroundColor: C.panel, borderColor: C.border, "--tw-divide-opacity": 1 }}>
            {FAQS.map((f, i) => (
              <div key={i} style={{ borderColor: C.border }} data-testid={`faq-${i}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 text-left px-5 py-4"
                  data-testid={`faq-toggle-${i}`}>
                  <span className="font-heading font-bold uppercase tracking-tight text-lg" style={{ color: C.text }}>{f.q}</span>
                  {openFaq === i ? <Minus className="w-4 h-4 shrink-0" style={{ color: C.oliveLight }} /> : <Plus className="w-4 h-4 shrink-0" style={{ color: C.muted }} />}
                </button>
                {openFaq === i && <p className="px-5 pb-5 -mt-1 text-sm" style={{ color: C.muted }}>{f.a}</p>}
              </div>
            ))}
          </div>
          </div>
        </section>
        </SectionBoundary>

        {/* 9 — CLOSING CTA */}
        <SectionBoundary>
        <section className="os-hairline relative overflow-hidden" data-testid="cta-section">
          <div className="relative max-w-[720px] mx-auto px-5 py-24 text-center flex flex-col items-center">
            <img src={LOGO} alt="OpticSwap" className="h-16 w-auto" data-testid="cta-logo" />
            {user ? (
              <>
                <h2 className="mt-6 font-heading font-bold uppercase tracking-tight text-4xl min-[1000px]:text-5xl" style={{ color: C.text }}>Back to the gear.</h2>
                <p className="mt-4 text-base max-w-lg" style={{ color: C.muted }}>Jump into the marketplace and pick up where you left off.</p>
                <div className="mt-8 flex flex-wrap gap-3 justify-center">
                  <OliveBtn to="/feed" data-testid="cta-marketplace">Browse Marketplace <ArrowRight className="w-4 h-4" /></OliveBtn>
                </div>
              </>
            ) : (
              <>
                <h2 className="mt-6 font-heading font-bold uppercase tracking-tight text-4xl min-[1000px]:text-5xl" style={{ color: C.text }}>Get your seat number.</h2>
                <p className="mt-4 text-base max-w-lg" style={{ color: C.muted }}>
                  Your seat number is yours — once it's claimed, it's gone.
                </p>
                <div className="mt-8 flex flex-wrap gap-3 justify-center">
                  <OliveBtn to="/register" data-testid="cta-create">Create your account <ArrowRight className="w-4 h-4" /></OliveBtn>
                  <OutlineBtn to="/login" data-testid="cta-login">Log in</OutlineBtn>
                </div>
              </>
            )}
          </div>
        </section>
        </SectionBoundary>
      </main>

      {/* 10 — FOOTER */}
      <footer style={{ backgroundColor: C.inset, borderTop: `1px solid ${C.border}` }}>
        <div className="max-w-[1240px] mx-auto px-5 py-14 grid sm:grid-cols-2 min-[1000px]:grid-cols-4 gap-10">
          <div>
            <Link to="/" aria-label="OpticSwap home" className="flex items-center gap-2.5">
              <img src={LOGO} alt="OpticSwap" className="h-9 w-auto" data-testid="footer-logo" />
              <span className="font-heading font-bold uppercase tracking-tight text-lg" style={{ color: C.text }}>OpticSwap</span>
            </Link>
            <p className="mt-4 text-sm max-w-xs" style={{ color: C.dim }}>
              The protected-payment marketplace for shooting optics and the gear that goes with them. Verified sellers, payments held until delivery.
            </p>
          </div>
          <FooterCol title="Marketplace" links={[
            { label: "Browse the feed", to: user ? "/feed" : "/join" },
            { label: "Pricing", to: "/pricing" },
            { label: "Map", to: "/map" },
          ]} />
          <FooterCol title="Social" links={[
            { label: "Ambassadors", to: "/ambassadors" },
            { label: "In The Wild", to: "/in-the-wild" },
            { label: "Community", to: user ? "/community" : "/join" },
          ]} />
          <FooterCol title="Company" links={[
            { label: "About", to: "/about" },
            { label: "FAQ", to: "/faq" },
            { label: "Terms", to: "/terms" },
            { label: "Privacy", to: "/privacy" },
          ]} />
        </div>
        <div className="border-t" style={{ borderColor: C.border }}>
          <div className="max-w-[1240px] mx-auto px-5 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
            <span className="font-mono uppercase text-[11px] tracking-[0.14em]" style={{ color: C.dim }}>© {new Date().getFullYear()} OpticSwap</span>
            <div className="flex items-center gap-5">
              <Link to="/terms" className="mkt-navlink font-mono uppercase text-[11px] tracking-[0.14em]" style={{ color: C.dim }}>Terms</Link>
              <Link to="/privacy" className="mkt-navlink font-mono uppercase text-[11px] tracking-[0.14em]" style={{ color: C.dim }}>Privacy</Link>
              <Link to="/faq" className="mkt-navlink font-mono uppercase text-[11px] tracking-[0.14em]" style={{ color: C.dim }}>Prohibited Items</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ---- Footer column -------------------------------------------------------- */
function FooterCol({ title, links }) {
  return (
    <div>
      <p className="font-mono uppercase text-[11px] tracking-[0.14em]" style={{ color: C.oliveLight }}>{title}</p>
      <ul className="mt-4 space-y-2.5">
        {links.map((l) => (
          <li key={l.label}>
            {l.href
              ? <a href={l.href} className="mkt-navlink text-sm" style={{ color: C.muted }}>{l.label}</a>
              : <Link to={l.to} className="mkt-navlink text-sm" style={{ color: C.muted }}>{l.label}</Link>}
          </li>
        ))}
      </ul>
    </div>
  );
}
